import { createApiRef } from '@backstage/core-plugin-api';
import type {
  DevxpConfig,
  DeveloperMapping,
  UnmaskResult,
  HashResult,
  UploadResult,
  GithubSyncConfig,
  GithubSyncResult,
  DashboardStats,
  DeveloperLeaderboard,
  LeaderboardFilters,
  DeveloperPerformance,
  DeveloperAveragePerformance,
  RepositoryList,
  RepositoryDetails,
  RepoMapping,
  ScanJobList,
  ScanJobDetails,
} from './types';

export interface DevxpApi {
  getConfig(): Promise<DevxpConfig>;
  getMappings(): Promise<{ mappings: DeveloperMapping[] }>;
  uploadCsv(csvContent: string): Promise<UploadResult>;
  unmask(maskedName: string): Promise<UnmaskResult>;
  hash(realName: string): Promise<HashResult>;
  deleteMapping(maskedName: string): Promise<{ message: string }>;
  // GitHub sync
  getGithubSyncConfigs(): Promise<{ configs: GithubSyncConfig[] }>;
  createGithubSyncConfig(orgName: string, githubHostname: string, appClientId: string, appPrivateKey: string): Promise<{ id: number; message: string }>;
  toggleGithubSyncConfig(id: number, active: boolean): Promise<{ message: string }>;
  deleteGithubSyncConfig(id: number): Promise<{ message: string }>;
  syncGithubConfig(id: number): Promise<GithubSyncResult>;
  triggerAutoSync(): Promise<{ message: string }>;
  // Analytics dashboard
  getDashboardStats(): Promise<DashboardStats>;
  getDeveloperLeaderboard(filters: Partial<LeaderboardFilters>): Promise<DeveloperLeaderboard>;
  getDeveloperPerformance(userId: string): Promise<DeveloperPerformance>;
  getDeveloperAveragePerformance(repoNames?: string[]): Promise<DeveloperAveragePerformance>;
  getRepositories(filters: { page?: number; pageSize?: number; searchQuery?: string }): Promise<RepositoryList>;
  getRepositoryDetails(repoName: string): Promise<RepositoryDetails>;
  getRepoMappings(): Promise<{ mappings: RepoMapping[] }>;
  unmaskRepo(maskedName: string): Promise<UnmaskResult>;
  uploadRepoCsv(csvContent: string): Promise<UploadResult>;
  getScanJobs(page?: number, pageSize?: number): Promise<ScanJobList>;
  getScanJobDetails(jobId: string): Promise<ScanJobDetails>;
}

export const devxpApiRef = createApiRef<DevxpApi>({
  id: 'plugin.devxp.api',
});

export class DevxpClient implements DevxpApi {
  private readonly fetchApi: { fetch: typeof fetch };
  private readonly discoveryApi: { getBaseUrl: (pluginId: string) => Promise<string> };

  constructor(options: {
    fetchApi: { fetch: typeof fetch };
    discoveryApi: { getBaseUrl: (pluginId: string) => Promise<string> };
  }) {
    this.fetchApi = options.fetchApi;
    this.discoveryApi = options.discoveryApi;
  }

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('devxp');
  }

  async getConfig(): Promise<DevxpConfig> {
    const url = `${await this.baseUrl()}/config`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch config: ${response.statusText}`);
    return response.json();
  }

  async getMappings(): Promise<{ mappings: DeveloperMapping[] }> {
    const url = `${await this.baseUrl()}/mappings`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch mappings: ${response.statusText}`);
    return response.json();
  }

  async uploadCsv(csvContent: string): Promise<UploadResult> {
    const url = `${await this.baseUrl()}/mappings/upload`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ csvContent }),
    });
    const data = await response.json();
    if (!response.ok) return { message: data.error || 'Upload failed', count: 0, error: data.error };
    return data;
  }

  async unmask(maskedName: string): Promise<UnmaskResult> {
    const url = `${await this.baseUrl()}/unmask`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ maskedName }),
    });
    if (!response.ok) throw new Error(`Failed to unmask: ${response.statusText}`);
    return response.json();
  }

  async hash(realName: string): Promise<HashResult> {
    const url = `${await this.baseUrl()}/hash`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ realName }),
    });
    if (!response.ok) throw new Error(`Failed to hash: ${response.statusText}`);
    return response.json();
  }

  async deleteMapping(maskedName: string): Promise<{ message: string }> {
    const url = `${await this.baseUrl()}/mappings/delete`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ maskedName }),
    });
    if (!response.ok) throw new Error(`Failed to delete mapping: ${response.statusText}`);
    return response.json();
  }

  // ─── GitHub Sync ─────────────────────────────────────────────────────────────

  async getGithubSyncConfigs(): Promise<{ configs: GithubSyncConfig[] }> {
    const url = `${await this.baseUrl()}/github-sync`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch GitHub sync configs: ${response.statusText}`);
    return response.json();
  }

  async createGithubSyncConfig(
    orgName: string,
    githubHostname: string,
    appClientId: string,
    appPrivateKey: string,
  ): Promise<{ id: number; message: string }> {
    const url = `${await this.baseUrl()}/github-sync`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orgName, githubHostname, appClientId, appPrivateKey }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to create GitHub sync config');
    return data;
  }

  async toggleGithubSyncConfig(id: number, active: boolean): Promise<{ message: string }> {
    const url = `${await this.baseUrl()}/github-sync/${id}/toggle`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to toggle GitHub sync config');
    return data;
  }

  async deleteGithubSyncConfig(id: number): Promise<{ message: string }> {
    const url = `${await this.baseUrl()}/github-sync/${id}`;
    const response = await this.fetchApi.fetch(url, {
      method: 'DELETE',
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to delete GitHub sync config');
    return data;
  }

  async syncGithubConfig(id: number): Promise<GithubSyncResult> {
    const url = `${await this.baseUrl()}/github-sync/${id}/sync`;
    const response = await this.fetchApi.fetch(url, { method: 'POST' });
    const data = await response.json();
    if (!response.ok) return { message: data.error || 'Sync failed', count: 0, repoCount: 0, orgName: '', error: data.error };
    return data;
  }

  async triggerAutoSync(): Promise<{ message: string }> {
    const url = `${await this.baseUrl()}/github-sync/auto`;
    const response = await this.fetchApi.fetch(url, { method: 'POST' });
    if (!response.ok) return { message: 'Auto-sync trigger failed' };
    return response.json();
  }

  async getDashboardStats(): Promise<DashboardStats> {
    const url = `${await this.baseUrl()}/analytics/dashboard`;
    const response = await this.fetchApi.fetch(url, { method: 'POST' });
    if (!response.ok) throw new Error(`Failed to fetch dashboard stats: ${response.statusText}`);
    return response.json();
  }

  async getDeveloperLeaderboard(filters: Partial<LeaderboardFilters> = {}): Promise<DeveloperLeaderboard> {
    const url = `${await this.baseUrl()}/analytics/leaderboard`;
    const body: Record<string, any> = {
      page: filters.page ?? 1,
      pageSize: filters.pageSize ?? 20,
      categories: filters.categories ?? [],
      repoNames: filters.repoNames ?? [],
      sortBy: filters.sortBy ?? 'avgProficiency',
      sortOrder: filters.sortOrder ?? 'desc',
      searchQuery: filters.searchQuery ?? '',
    };
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`Failed to fetch leaderboard: ${response.statusText}`);
    return response.json();
  }

  async getDeveloperPerformance(userId: string): Promise<DeveloperPerformance> {
    const url = `${await this.baseUrl()}/analytics/developer/performance`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId }),
    });
    if (!response.ok) throw new Error(`Failed to fetch developer performance: ${response.statusText}`);
    return response.json();
  }

  async getDeveloperAveragePerformance(repoNames: string[] = []): Promise<DeveloperAveragePerformance> {
    const url = `${await this.baseUrl()}/analytics/developer/performance/average`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ repoNames }),
    });
    if (!response.ok) throw new Error(`Failed to fetch average performance: ${response.statusText}`);
    return response.json();
  }

  async getRepositories(filters: { page?: number; pageSize?: number; searchQuery?: string } = {}): Promise<RepositoryList> {
    const url = `${await this.baseUrl()}/analytics/repositories`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page: filters.page ?? 1,
        pageSize: filters.pageSize ?? 50,
        searchQuery: filters.searchQuery ?? '',
      }),
    });
    if (!response.ok) throw new Error(`Failed to fetch repositories: ${response.statusText}`);
    return response.json();
  }

  async getRepositoryDetails(repoName: string): Promise<RepositoryDetails> {
    const url = `${await this.baseUrl()}/analytics/repository/details`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ repoName }),
    });
    if (!response.ok) throw new Error(`Failed to fetch repository details: ${response.statusText}`);
    return response.json();
  }

  async getRepoMappings(): Promise<{ mappings: RepoMapping[] }> {
    const url = `${await this.baseUrl()}/repo-mappings`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch repo mappings: ${response.statusText}`);
    return response.json();
  }

  async unmaskRepo(maskedName: string): Promise<UnmaskResult> {
    const url = `${await this.baseUrl()}/unmask-repo`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ maskedName }),
    });
    if (!response.ok) throw new Error(`Failed to unmask repo: ${response.statusText}`);
    return response.json();
  }

  async uploadRepoCsv(csvContent: string): Promise<UploadResult> {
    const url = `${await this.baseUrl()}/repo-mappings/upload`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ csvContent }),
    });
    const data = await response.json();
    if (!response.ok) return { message: data.error || 'Upload failed', count: 0, error: data.error };
    return data;
  }

  async getScanJobs(page: number = 1, pageSize: number = 20): Promise<ScanJobList> {
    const url = `${await this.baseUrl()}/analytics/scan-jobs`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page, pageSize }),
    });
    if (!response.ok) throw new Error(`Failed to fetch scan jobs: ${response.statusText}`);
    return response.json();
  }

  async getScanJobDetails(jobId: string): Promise<ScanJobDetails> {
    const url = `${await this.baseUrl()}/analytics/scan-job/details`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jobId }),
    });
    if (!response.ok) throw new Error(`Failed to fetch scan job details: ${response.statusText}`);
    return response.json();
  }
}
